import { useEffect, useMemo, useState } from "react"
import { Card, Button, Space } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { Toast } from "@/components/Toast"
import DomainTable from "./components/DomainTable"
import DomainModal from "./components/DomainModal"
import { useCrud } from "@/hooks/useCrud"
import { DomainLv1, DomainLv1FormData } from "./types"
import domainLv1Api from "./api"
import { useDomainLv1 } from "@/hooks/useDomainLv1"
import { ID } from "@/types/common"

const DomainManagement = () => {
  // 获取全局一级领域数据的刷新方法
  const { invalidate: refreshDomainLv1s } = useDomainLv1()
  const [assistTypeOptions, setAssistTypeOptions] = useState<
    Array<{ id: ID; code: string; label: string; enabled: boolean }>
  >([])

  // 使用 useMemo 稳定 api 对象，避免重复请求
  const api = useMemo(
    () => ({
      getList: () => domainLv1Api.getList(),
      create: domainLv1Api.create,
      update: (
        id: ID,
        data: DomainLv1FormData | Partial<DomainLv1FormData>
      ) => domainLv1Api.update(id, data as DomainLv1FormData),
      delete: domainLv1Api.delete,
    }),
    []
  )

  const {
    items: domainLv1s,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingDomainLv1,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave: handleCrudSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
  } = useCrud<DomainLv1, DomainLv1FormData, ID>({
    api,
    entityName: "一级领域",
    hooks: {
      afterSave: () => {
        // 保存后刷新全局一级领域数据
        refreshDomainLv1s()
      },
      afterDelete: () => {
        // 删除后刷新全局一级领域数据
        refreshDomainLv1s()
      },
      afterBatchDelete: () => {
        // 批量删除后刷新全局一级领域数据
        refreshDomainLv1s()
      },
    },
  })

  useEffect(() => {
    const loadAssistTypeOptions = async () => {
      try {
        const options = await domainLv1Api.getAssistTypeOptions()
        setAssistTypeOptions(options)
      } catch (error) {
        console.error("加载辅助类型字典项失败:", error)
      }
    }

    loadAssistTypeOptions()
  }, [])

  const handleSave = async (data: DomainLv1FormData) => {
    const { assistTypeItemIds, ...payload } = data
    await handleCrudSave(payload)
    if (!editingDomainLv1) {
      return
    }
    try {
      await domainLv1Api.updateAssistTypeBindings(
        editingDomainLv1.id,
        assistTypeItemIds || []
      )
    } catch (error) {
      Toast.error("一级领域保存成功，但辅助类型绑定更新失败")
      console.error("更新一级领域辅助类型绑定失败:", error)
    }
  }

  // 计算统计数据
  const totalDomainLv1s = domainLv1s.length
  const selectedCount = selectedIds.length
  const batchDisabled = selectedCount === 0
  return (
    <div className="p-6">
      {/* 主要内容区域 */}
      <Card>
        {/* 操作栏 */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <span className="text-gray-500">
              共 {totalDomainLv1s} 个一级领域
            </span>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
              data-testid="btn-add-domain"
            >
              新增一级领域
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={batchDisabled}
              loading={opLoading}
              data-testid="btn-batch-delete"
            >
              批量删除 {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </Space>
        </div>

        {/* 一级领域表格 */}
        <DomainTable
          domainLv1s={domainLv1s}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      {/* 一级领域信息模态框 */}
      <DomainModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        domainLv1={editingDomainLv1}
        loading={opLoading}
        assistTypeOptions={assistTypeOptions}
      />
    </div>
  )
}

export default DomainManagement
